import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/shadcn/tabs'
import { AdminAppsumoTab } from './admin-appsumo-tab'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { Input } from '@/components/ui/shadcn/input'
import { Button } from '@/components/ui/shadcn/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { CheckCircle, XCircle, PlusCircle, Edit, Clock, Copy, Send } from 'lucide-react'
import { useState } from 'react'
import { type RouterOutput, trpc } from '@/config/trpc'
import { toast } from 'sonner'
import { useNavigate, useSearchParams } from 'react-router'

type Subscription = RouterOutput['userSubscription']['list'][0]

type GrantTier = 'standard' | 'pro' | 'pro-trial' | 'enterprise'

const TIER_OPTIONS: { value: GrantTier; label: string }[] = [
  { value: 'pro-trial', label: 'Pro Trial' },
  { value: 'standard', label: 'Standard' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
]

const ADMIN_TABS = ['users', 'subscriptions', 'appsumo', 'test-accounts'] as const

const CUSTOM_PLATFORM = 'custom'

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google-drive', label: 'Google Drive' },
  { value: CUSTOM_PLATFORM, label: 'Custom…' },
] as const

export function AdminPage() {
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab = ADMIN_TABS.includes(tabParam as (typeof ADMIN_TABS)[number])
    ? tabParam!
    : 'subscriptions'

  const [showAddSubscription, setShowAddSubscription] = useState(false)
  const [email, setEmail] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [tier, setTier] = useState<GrantTier>('pro-trial')

  const [showEditSubscription, setShowEditSubscription] = useState(false)
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null)
  const [editExpiresAt, setEditExpiresAt] = useState('')
  const [editTier, setEditTier] = useState<GrantTier>('pro-trial')

  const [showAddTestAccount, setShowAddTestAccount] = useState(false)
  const [testPlatform, setTestPlatform] = useState('')
  const [customPlatform, setCustomPlatform] = useState('')
  const [testPassword, setTestPassword] = useState('')

  const [reminderEmail, setReminderEmail] = useState('')

  const utils = trpc.useUtils()
  const { data: users } = trpc.user.list.useQuery()
  const { data: userSubscriptions } = trpc.userSubscription.list.useQuery()
  const { data: testUsers } = trpc.user.listTestUsers.useQuery()

  const { mutateAsync: createSubscription } = trpc.userSubscription.create.useMutation({
    onSuccess: () => utils.userSubscription.list.invalidate(),
  })
  const { mutateAsync: updateSubscription } = trpc.userSubscription.update.useMutation({
    onSuccess: () => utils.userSubscription.list.invalidate(),
  })
  const { mutateAsync: deleteSubscription } = trpc.userSubscription.delete.useMutation({
    onSuccess: () => utils.userSubscription.list.invalidate(),
  })

  const { mutateAsync: createTestUser } = trpc.user.createTestUser.useMutation({
    onSuccess: () => {
      utils.user.listTestUsers.invalidate()
      utils.user.list.invalidate()
    },
  })

  const { mutateAsync: deleteTestUser } = trpc.user.deleteTestUser.useMutation({
    onSuccess: () => {
      utils.user.listTestUsers.invalidate()
      utils.user.list.invalidate()
    },
  })

  const { mutateAsync: triggerReminder, isPending: isTriggeringReminder } =
    trpc.user.triggerReminder.useMutation()

  const handleAddSubscription = async () => {
    if (!email) return
    try {
      // Empty expiry = the grant never expires.
      await createSubscription({
        email: email,
        expiresAt: expiresAt || undefined,
        tier: tier,
      })

      setEmail('')
      setExpiresAt('')
      setTier('pro-trial')
      setShowAddSubscription(false)

      toast.success('Subscription saved successfully')
    } catch (error) {
      toast.error('Failed to save subscription', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  const handleEditSubscription = async () => {
    if (!editingSubscription) return

    try {
      await updateSubscription({
        userId: editingSubscription.userId,
        tier: editTier,
        // Empty expiry clears the date — the grant never expires.
        expiresAt: editExpiresAt || null,
      })

      setShowEditSubscription(false)
      setEditingSubscription(null)
      setEditExpiresAt('')

      toast.success('Subscription updated successfully')
    } catch (error) {
      toast.error('Failed to update subscription', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  const handleDeleteSubscription = async (subscription: Subscription) => {
    if (
      !confirm(
        `Delete the ${subscription.tier} subscription of ${subscription.userEmail}? They drop to the free plan immediately.`,
      )
    ) {
      return
    }

    try {
      await deleteSubscription({ userId: subscription.userId })

      toast.success('Subscription deleted successfully')
    } catch (error) {
      toast.error('Failed to delete subscription', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  const resolvedTestPlatform = (
    testPlatform === CUSTOM_PLATFORM ? customPlatform : testPlatform
  ).trim()

  const handleCreateTestAccount = async () => {
    if (!resolvedTestPlatform) return

    try {
      const result = await createTestUser({
        platform: resolvedTestPlatform,
        password: testPassword || undefined,
      })

      setTestPlatform('')
      setCustomPlatform('')
      setTestPassword('')
      setShowAddTestAccount(false)

      toast.success('Test account created successfully', {
        description: `Email: ${result.email}\nPassword: ${result.password}`,
      })
    } catch (error) {
      toast.error('Failed to create test account', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  const handleDeleteTestAccount = async (userId: number, email: string) => {
    if (!confirm(`Are you sure you want to delete test account: ${email}?`)) {
      return
    }

    try {
      await deleteTestUser({ userId })
      toast.success('Test account deleted successfully')
    } catch (error) {
      toast.error('Failed to delete test account', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  const handleTriggerReminder = async () => {
    if (!reminderEmail) return
    try {
      const result = await triggerReminder({ email: reminderEmail })
      setReminderEmail('')
      toast.success(`Reminder triggered for ${result.processed} automation(s)`, {
        description: result.results
          .map((r) => `Automation ${r.automationId} → Workspace ${r.workspaceId}`)
          .join('\n'),
      })
    } catch (error) {
      toast.error('Failed to trigger reminder', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} copied to clipboard`)
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleTabChange = (value: string) => {
    setSearchParams(
      (prev) => {
        prev.set('tab', value)
        return prev
      },
      { replace: true },
    )
  }

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-8">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="mt-1 text-gray-500">Manage users and subscriptions</p>
        </header>

        <div className="mb-2 flex items-center gap-2">
          <Button onClick={() => navigate('/admin/langflow')}>Langflow Playground</Button>
          <Button onClick={() => navigate('/admin/logs')}>Logs</Button>
        </div>

        <Tabs defaultValue="users" value={activeTab} onValueChange={handleTabChange}>
          <div className="mb-4 flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="users" className="px-6">
                Users
              </TabsTrigger>
              <TabsTrigger value="subscriptions" className="px-6">
                Subscriptions
              </TabsTrigger>
              <TabsTrigger value="appsumo" className="px-6">
                AppSumo
              </TabsTrigger>
              <TabsTrigger value="test-accounts" className="px-6">
                Test Accounts
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-3">
              {activeTab === 'users' && (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    handleTriggerReminder()
                  }}>
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    className="w-64"
                    value={reminderEmail}
                    onChange={(e) => setReminderEmail(e.target.value)}
                    required
                  />
                  <Button type="submit" disabled={!reminderEmail || isTriggeringReminder}>
                    <Send className="mr-2 h-4 w-4" />
                    {isTriggeringReminder ? 'Generating...' : 'Trigger Reminder'}
                  </Button>
                </form>
              )}
              {activeTab === 'subscriptions' && (
                <Button onClick={() => setShowAddSubscription(true)}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Subscription
                </Button>
              )}
              {activeTab === 'test-accounts' && (
                <Button onClick={() => setShowAddTestAccount(true)}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Create Test Account
                </Button>
              )}
            </div>
          </div>

          <TabsContent value="users" className="mt-0">
            <Card>
              <CardHeader className="bg-card border-b px-6 py-4">
                <CardTitle className="item-center flex w-full justify-between">
                  Users
                  <span>{users?.length} users</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-card text-muted-foreground text-xs uppercase">
                      <tr>
                        <th scope="col" className="w-[50px] px-6 py-3">
                          ID
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Email
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Verified
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Created At
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* This would be populated from your backend data */}
                      {users
                        ?.slice() // create a shallow copy to avoid mutating original array
                        .sort(
                          (a, b) =>
                            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                        )
                        .map((user) => (
                          <tr className="hover:bg-card bg-card border-b" key={user.id}>
                            <td className="px-6 py-4 font-medium">{user.id}</td>
                            <td className="px-6 py-4">{user.email}</td>
                            <td className="px-6 py-4">
                              {user.veryfied ? (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                  <CheckCircle className="mr-1 h-3.5 w-3.5" />
                                  Verified
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                  <XCircle className="mr-1 h-3.5 w-3.5" />
                                  Not Verified
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {new Date(user.createdAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-0">
            <Card>
              <CardHeader className="bg-card border-b px-6 py-4">
                <CardTitle className="item-center flex w-full justify-between">
                  Subscriptions
                  <span>{userSubscriptions?.length} subscriptions</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Dialog
                  open={showAddSubscription}
                  onOpenChange={(o) => !o && setShowAddSubscription(false)}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add Subscription</DialogTitle>
                      <DialogDescription>
                        Grants a magig-sourced subscription. If the user already has a magig grant
                        (e.g. an old trial), it is updated in place.
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        handleAddSubscription()
                      }}>
                      <div className="grid gap-4 py-2">
                        <div>
                          <label className="text-muted-foreground mb-1 block text-sm font-medium">
                            User Email
                          </label>
                          <Input
                            type="email"
                            list="admin-user-emails"
                            placeholder="Start typing to search users"
                            className="w-full"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                          <datalist id="admin-user-emails">
                            {users?.map((u) => (
                              <option key={u.id} value={u.email} />
                            ))}
                          </datalist>
                        </div>

                        <div>
                          <label className="text-muted-foreground mb-1 block text-sm font-medium">
                            Tier
                          </label>
                          <Select value={tier} onValueChange={(v) => setTier(v as GrantTier)}>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIER_OPTIONS.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-muted-foreground mb-1 block text-sm font-medium">
                            Expires At
                          </label>
                          <Input
                            type="date"
                            className="w-full"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                          />
                          <p className="text-muted-foreground mt-1 text-xs">
                            Leave empty for a grant that never expires.
                          </p>
                        </div>
                      </div>

                      <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowAddSubscription(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={!email}>
                          Save Subscription
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog
                  open={showEditSubscription}
                  onOpenChange={(o) => {
                    if (!o) {
                      setShowEditSubscription(false)
                      setEditingSubscription(null)
                    }
                  }}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Edit Subscription</DialogTitle>
                      <DialogDescription>{editingSubscription?.userEmail}</DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        handleEditSubscription()
                      }}>
                      <div className="grid gap-4 py-2">
                        <div>
                          <label className="text-muted-foreground mb-1 block text-sm font-medium">
                            Tier
                          </label>
                          <Select
                            value={editTier}
                            onValueChange={(v) => setEditTier(v as GrantTier)}>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIER_OPTIONS.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-muted-foreground mb-1 block text-sm font-medium">
                            Expires At
                          </label>
                          <Input
                            type="date"
                            className="w-full"
                            value={editExpiresAt}
                            onChange={(e) => setEditExpiresAt(e.target.value)}
                          />
                          <p className="text-muted-foreground mt-1 text-xs">
                            Leave empty for a grant that never expires.
                          </p>
                        </div>
                      </div>

                      <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowEditSubscription(false)
                            setEditingSubscription(null)
                          }}>
                          Cancel
                        </Button>
                        <Button type="submit">Save Changes</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-card text-muted-foreground text-xs uppercase">
                      <tr>
                        <th scope="col" className="w-[100px] px-6 py-3">
                          user id
                        </th>
                        <th scope="col" className="px-6 py-3">
                          email
                        </th>
                        <th scope="col" className="px-6 py-3">
                          source
                        </th>
                        <th scope="col" className="px-6 py-3">
                          tier
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Expires At
                        </th>
                        {/* Added Actions column */}
                        <th scope="col" className="px-6 py-3 text-center">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {userSubscriptions?.map((subscription, i) => (
                        <tr className="hover:bg-card bg-card border-b" key={i}>
                          <td className="px-6 py-4 font-medium">{subscription.userId}</td>
                          <td className="px-6 py-4">{subscription.userEmail}</td>
                          <td className="px-6 py-4">
                            {subscription.source === 'stripe' && (
                              <span className="bg-blue inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium text-white">
                                stripe
                              </span>
                            )}
                            {subscription.source === 'magig' && (
                              <span className="inline-flex items-center rounded-md bg-[#800080] px-2.5 py-0.5 text-xs font-medium text-white">
                                magig
                              </span>
                            )}
                            {subscription.source === 'code' && (
                              <span className="bg-card inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium text-gray-600">
                                code
                              </span>
                            )}
                            {subscription.source === 'appsumo' && (
                              <span className="inline-flex items-center rounded-md bg-[#ffbc00] px-2.5 py-0.5 text-xs font-medium text-black">
                                appsumo
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">{subscription.tier}</td>
                          <td className="px-6 py-4">
                            {subscription.status === 'active' && (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                <CheckCircle className="mr-1 h-3.5 w-3.5" />
                                Active
                              </span>
                            )}

                            {subscription.status === 'expiring soon' && (
                              <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                                <Clock className="mr-1 h-3.5 w-3.5" />
                                Expiring Soon
                              </span>
                            )}

                            {subscription.status === 'expired' && (
                              <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                <XCircle className="mr-1 h-3.5 w-3.5" />
                                Expired
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {subscription.expiresAt
                              ? new Date(subscription.expiresAt).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })
                              : 'No expiration date'}
                          </td>
                          <td className="flex items-center justify-center px-6 py-4">
                            {subscription.source === 'magig' && (
                              <span className="item-center flex gap-1">
                                <button
                                  title="Edit subscription"
                                  className="mr-2 font-medium text-purple-600 hover:text-purple-800"
                                  onClick={() => {
                                    setEditingSubscription(subscription)
                                    setEditTier(subscription.tier as GrantTier)
                                    setEditExpiresAt(
                                      subscription.expiresAt
                                        ? (new Date(subscription.expiresAt)
                                            .toISOString()
                                            .split('T')[0] ?? '')
                                        : '',
                                    )

                                    setShowEditSubscription(true)
                                  }}>
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  title="Delete subscription (drops the user to free)"
                                  className="mr-2 font-medium text-red-600 hover:text-red-800"
                                  onClick={() => {
                                    handleDeleteSubscription(subscription)
                                  }}>
                                  <XCircle className="h-4 w-4" />
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="appsumo" className="mt-0">
            <AdminAppsumoTab />
          </TabsContent>

          <TabsContent value="test-accounts" className="mt-0">
            <Card>
              <CardHeader className="bg-card border-b px-6 py-4">
                <CardTitle className="item-center flex w-full justify-between">
                  Test Accounts
                  <span>{testUsers?.length} test accounts</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {showAddTestAccount && (
                  <div className="bg-card border-b p-6">
                    <h3 className="mb-4 text-lg font-medium">Create Test Account</h3>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        handleCreateTestAccount()
                      }}>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-muted-foreground mb-1 block text-sm font-medium">
                            Platform
                          </label>
                          <Select value={testPlatform} onValueChange={setTestPlatform} required>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select platform" />
                            </SelectTrigger>
                            <SelectContent>
                              {PLATFORMS.map((platform) => (
                                <SelectItem key={platform.value} value={platform.value}>
                                  {platform.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {testPlatform === CUSTOM_PLATFORM && (
                            <Input
                              type="text"
                              placeholder="Enter custom platform name"
                              className="mt-2 w-full"
                              value={customPlatform}
                              onChange={(e) => setCustomPlatform(e.target.value)}
                              required
                            />
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            Email will be: test##-{resolvedTestPlatform || '{platform}'}
                            @test.getcassi.ai
                          </p>
                        </div>

                        <div>
                          <label className="text-muted-foreground mb-1 block text-sm font-medium">
                            Password (optional)
                          </label>
                          <Input
                            type="text"
                            placeholder="Leave empty to auto-generate secure password"
                            className="w-full"
                            value={testPassword}
                            onChange={(e) => setTestPassword(e.target.value)}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Auto-generated: 15 chars with lowercase, uppercase, numbers & special
                            chars
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowAddTestAccount(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={!resolvedTestPlatform}>
                          Create Test Account
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-card text-muted-foreground text-xs uppercase">
                      <tr>
                        <th scope="col" className="w-[50px] px-6 py-3">
                          ID
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Email
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Platform
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Password
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Verified
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Created At
                        </th>
                        <th scope="col" className="px-6 py-3 text-center">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {testUsers?.map((user) => {
                        // Extract platform from email (test##-platform@test.getcassi.ai)
                        const platformMatch = user.email.match(/test\d+-(.+)@/)
                        const platform = platformMatch ? platformMatch[1] : 'unknown'

                        return (
                          <tr className="hover:bg-card bg-card border-b" key={user.id}>
                            <td className="px-6 py-4 font-medium">{user.id}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {user.email}
                                <button
                                  onClick={() => copyToClipboard(user.email, 'Email')}
                                  className="text-gray-400 hover:text-gray-600">
                                  <Copy className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center rounded-md bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                                {platform}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <code className="rounded bg-gray-100 px-2 py-1 text-xs dark:bg-gray-800 dark:text-gray-200">
                                  {user.password || 'N/A'}
                                </code>
                                {user.password && (
                                  <button
                                    onClick={() => copyToClipboard(user.password!, 'Password')}
                                    className="text-gray-400 hover:text-gray-600">
                                    <Copy className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {user.verified ? (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                  <CheckCircle className="mr-1 h-3.5 w-3.5" />
                                  Verified
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                  <XCircle className="mr-1 h-3.5 w-3.5" />
                                  Not Verified
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {new Date(user.createdAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => handleDeleteTestAccount(user.id, user.email)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete test account">
                                <XCircle className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
